import React, { forwardRef, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { es, enUS, ptBR } from 'date-fns/locale';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { 
  FaCalendarAlt, 
  FaClock, 
  FaChevronLeft, 
  FaChevronRight,
  FaTimes 
} from 'react-icons/fa';
import './DatePicker.scss';

// Custom input component for better styling
const CustomInput = forwardRef(({ 
  value, 
  onClick, 
  placeholder, 
  error, 
  label, 
  showTimeSelect, 
  clearable,
  onClear,
  onChange,
  onBlur,
  onKeyDown,
  disabled,
  required,
  id,
  clearLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-labelledby': ariaLabelledBy
}, ref) => (
  <div className="foth-date-input-container">
    {label && (
      <label className="foth-date-label" htmlFor={id}>
        {label}
        {required && <span className="required" aria-hidden="true"> *</span>}
      </label>
    )}
    <div className={`foth-date-input-wrapper ${error ? 'error' : ''} ${disabled ? 'disabled' : ''}`}>
      <input
        ref={ref}
        id={id}
        aria-labelledby={ariaLabelledBy}
        aria-invalid={error ? true : undefined}
        aria-describedby={[ariaDescribedBy, error && `${id}-error`].filter(Boolean).join(' ') || undefined}
        required={required}
        value={value}
        onClick={onClick}
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className="foth-date-input"
      />
      <div className="foth-date-icons">
        {clearable && value && !disabled && (
          <button 
            type="button" 
            onClick={onClear} 
            className="foth-clear-btn"
            aria-label={clearLabel}
          >
            <FaTimes />
          </button>
        )}
        <div className="foth-date-icon">
          {showTimeSelect ? <FaClock /> : <FaCalendarAlt />}
        </div>
      </div>
    </div>
    {error && <div id={`${id}-error`} className="foth-date-error">{error}</div>}
  </div>
));

CustomInput.displayName = 'CustomInput';

const LocalizedCalendarContainer = ({ className, children, inline, showTimeSelectOnly, showTime }) => {
  const { t } = useTranslation('common');
  return <div className={className} role={inline ? undefined : 'dialog'} aria-modal={inline ? undefined : true}
    aria-label={t(showTimeSelectOnly ? 'ui.selectTime' : showTime ? 'ui.selectDateTime' : 'ui.selectDate')}>
    {children}
  </div>;
};

// Custom header for the calendar
const CustomHeader = ({
  date,
  changeYear,
  changeMonth,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
  minDate,
  maxDate,
}) => {
  const currentYear = new Date().getFullYear();
  const { t, i18n } = useTranslation('common');
  const minYear = minDate instanceof Date ? minDate.getFullYear() : currentYear - 5;
  const maxYear = maxDate instanceof Date ? maxDate.getFullYear() : currentYear + 5;
  const yearCount = Math.max(maxYear - minYear + 1, 1);

  return (
    <div className="foth-datepicker-header">
      <button
        type="button"
        onClick={decreaseMonth}
        aria-label={t('ui.previousMonth')}
        disabled={prevMonthButtonDisabled}
        className="foth-nav-btn"
      >
        <FaChevronLeft />
      </button>
      
      <div className="foth-date-selectors">
        <select
          value={date.getFullYear()}
          aria-label={t('ui.year')}
          onChange={({ target: { value } }) => changeYear(value)}
          className="foth-year-select"
        >
          {Array.from({ length: yearCount }, (_, i) => {
            const year = minYear + i;
            return (
              <option key={year} value={year}>
                {year}
              </option>
            );
          })}
        </select>
        
        <select
          value={date.getMonth()}
          aria-label={t('ui.month')}
          onChange={({ target: { value } }) => changeMonth(value)}
          className="foth-month-select"
        >
          {Array.from({ length: 12 }, (_, i) => (
            <option key={i} value={i}>
              {new Date(2000, i).toLocaleDateString(i18n.resolvedLanguage || 'es', { month: 'long' })}
            </option>
          ))}
        </select>
      </div>
      
      <button
        type="button"
        onClick={increaseMonth}
        aria-label={t('ui.nextMonth')}
        disabled={nextMonthButtonDisabled}
        className="foth-nav-btn"
      >
        <FaChevronRight />
      </button>
    </div>
  );
};

const getEarlierDate = (...dates) => {
  const validDates = dates.filter(date => date instanceof Date && !Number.isNaN(date.getTime()));
  if (!validDates.length) return undefined;
  return new Date(Math.min(...validDates.map(date => date.getTime())));
};

const getLaterDate = (...dates) => {
  const validDates = dates.filter(date => date instanceof Date && !Number.isNaN(date.getTime()));
  if (!validDates.length) return undefined;
  return new Date(Math.max(...validDates.map(date => date.getTime())));
};

// Main DatePicker component
export const FothDatePicker = ({
  value,
  onChange,
  label,
  placeholder,
  error,
  showTimeSelect = false,
  timeFormat = "HH:mm",
  dateFormat = showTimeSelect ? "MMM d, yyyy h:mm aa" : "MMM d, yyyy",
  minDate,
  maxDate,
  clearable = true,
  disabled = false,
  required = false,
  excludeDates = [],
  highlightDates = [],
  className = "",
  id,
  'aria-labelledby': ariaLabelledBy,
  ...props
}) => {
  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
  };
  const { t, i18n } = useTranslation('common');
  const generatedId = useId();
  const inputId = id || generatedId;
  const locale = { es, en: enUS, pt: ptBR }[i18n.resolvedLanguage?.split('-')[0]] || es;

  return (
    <div className={`foth-datepicker ${className}`}>
      <DatePicker
        id={inputId}
        ariaLabelledBy={ariaLabelledBy}
        placeholderText={placeholder ?? t(props.showTimeSelectOnly ? 'ui.selectTime' : showTimeSelect ? 'ui.selectDateTime' : 'ui.selectDate')}
        timeCaption={props.timeCaption ?? t('ui.time')}
        locale={locale}
        calendarContainer={LocalizedCalendarContainer}
        chooseDayAriaLabelPrefix={t('ui.chooseDay')}
        disabledDayAriaLabelPrefix={t('ui.unavailableDay')}
        monthAriaLabelPrefix={t('ui.month')}
        required={required}
        selected={value}
        onChange={onChange}
        showTimeSelect={showTimeSelect}
        timeFormat={timeFormat}
        dateFormat={dateFormat}
        minDate={minDate}
        maxDate={maxDate}
        excludeDates={excludeDates}
        highlightDates={highlightDates}
        disabled={disabled}
        customInput={
          <CustomInput
            label={label}
            placeholder={placeholder}
            error={error}
            showTimeSelect={showTimeSelect}
            clearable={clearable}
            clearLabel={t('ui.clearDate')}
            onClear={handleClear}
            disabled={disabled}
            required={required}
            id={inputId}
            aria-labelledby={ariaLabelledBy}
          />
        }
        renderCustomHeader={(headerProps) => (
          <CustomHeader {...headerProps} minDate={minDate} maxDate={maxDate} />
        )}
        calendarClassName="foth-calendar"
        wrapperClassName="foth-datepicker-wrapper"
        popperClassName="foth-datepicker-popper"
        popperPlacement="bottom-start"
        {...props}
      />
    </div>
  );
};

// Time-only picker component
export const FothTimePicker = ({
  value,
  onChange,
  label,
  placeholder,
  error,
  timeIntervals = 15,
  timeCaption,
  disabled = false,
  required = false,
  className = "",
  ...props
}) => {
  return (
    <FothDatePicker
      value={value}
      onChange={onChange}
      label={label}
      placeholder={placeholder}
      error={error}
      showTimeSelect={true}
      showTimeSelectOnly={true}
      timeIntervals={timeIntervals}
      {...(timeCaption ? { timeCaption } : {})}
      dateFormat="h:mm aa"
      disabled={disabled}
      required={required}
      className={`foth-timepicker ${className}`}
      {...props}
    />
  );
};

// Date range picker component
export const FothDateRangePicker = ({
  startDate,
  endDate,
  onChange,
  startLabel,
  endLabel,
  startPlaceholder,
  endPlaceholder,
  startError,
  endError,
  maxDays,
  minDate,
  maxDate,
  disabled = false,
  required = false,
  className = "",
  startId,
  endId,
  ...props
}) => {
  const handleStartChange = (date) => {
    let newEndDate = endDate;
    
    // If end date is before start date, clear it
    if (endDate && date && endDate < date) {
      newEndDate = null;
    }
    
    // If maxDays is set, limit the end date
    if (maxDays && date) {
      const maxEndDate = new Date(date);
      maxEndDate.setDate(maxEndDate.getDate() + maxDays);
      if (endDate && endDate > maxEndDate) {
        newEndDate = maxEndDate;
      }
    }
    
    onChange({ startDate: date, endDate: newEndDate });
  };

  const handleEndChange = (date) => {
    onChange({ startDate, endDate: date });
  };

  const getMaxEndDate = () => {
    if (!maxDays || !startDate) return maxDate;
    const maxEndDate = new Date(startDate);
    maxEndDate.setDate(maxEndDate.getDate() + maxDays);
    return maxEndDate;
  };
  const { t } = useTranslation('common');

  const validStartConstraint = startDate instanceof Date
    && !Number.isNaN(startDate.getTime())
    && (!minDate || startDate >= minDate)
    ? startDate
    : undefined;
  // The start date must remain editable even when it moves past the current
  // end date. handleStartChange will clear the end date and let the user pick
  // a new valid range instead of silently blocking the input.
  const startMaxDate = maxDate;
  const endMinDate = getLaterDate(validStartConstraint, minDate);
  const endMaxDate = getEarlierDate(getMaxEndDate(), maxDate);

  return (
    <div className={`foth-daterange-picker ${className}`}>
      <div className="foth-daterange-inputs">
        <FothDatePicker
          value={startDate}
          onChange={handleStartChange}
          label={startLabel ?? t('ui.startDate')}
          placeholder={startPlaceholder}
          error={startError}
          minDate={minDate}
          maxDate={startMaxDate}
          disabled={disabled}
          required={required}
          id={startId}
          highlightDates={endDate ? [endDate] : []}
          {...props}
        />
        
        <FothDatePicker
          value={endDate}
          onChange={handleEndChange}
          label={endLabel ?? t('ui.endDate')}
          placeholder={endPlaceholder}
          error={endError}
          minDate={endMinDate}
          maxDate={endMaxDate}
          disabled={disabled || !validStartConstraint}
          required={required}
          id={endId}
          highlightDates={startDate ? [startDate] : []}
          {...props}
        />
      </div>
      
      {maxDays && startDate && (
        <div className="foth-daterange-info">
          <small>{t('ui.maxDuration', { count: maxDays })}</small>
        </div>
      )}
    </div>
  );
};

// DateTime picker component
export const FothDateTimePicker = ({
  value,
  onChange,
  label,
  placeholder,
  error,
  timeIntervals = 15,
  minDate,
  maxDate,
  disabled = false,
  required = false,
  className = "",
  ...props
}) => {
  return (
    <FothDatePicker
      value={value}
      onChange={onChange}
      label={label}
      placeholder={placeholder}
      error={error}
      showTimeSelect={true}
      timeIntervals={timeIntervals}
      minDate={minDate}
      maxDate={maxDate}
      disabled={disabled}
      required={required}
      className={`foth-datetimepicker ${className}`}
      {...props}
    />
  );
};

// Export default
export default FothDatePicker;
